import React, { useState, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";

import { HvButton, HvInput } from "@hitachivantara/uikit-react-core";
import { Add } from "@hitachivantara/uikit-react-icons";

import { useSnackbar } from "lib/provider/SnackbarProvider";

import AddEditCategoryDialog, {
  CategoryFormMode,
  CategoryFormData,
  CategoryFormType,
} from "../AddEditCategoryDialog";

import useStyles from "./styles";

interface CategoryControlsProps {
  type: CategoryFormType;
  onAdd: () => void;
  onSave: (data: CategoryFormData) => Promise<unknown>;
  onShouldRevalidate?: () => unknown;
  onAddEditDialogClose: () => void;
  mode?: CategoryFormMode;
  editingFormData?: CategoryFormData;
}

interface TableControls {
  onEnterHandler?: (value: string) => void;
}

type TableControlProps = CategoryControlsProps & TableControls;

/**
 * Controls on top of the Incident Type Category Management table.
 *
 * While the use cases remain similar, it is shared with the Incident Type Subcategory Management,
 * by switching the type paramenter.
 *
 * For now it only includes the Add functionality. Future use cases include the search inputbox and the filter panel.
 *
 * @param type Indicates if the controlling subject is Category or Subcategory.
 *             Mainly used for i18n, as it is the various callbacks that deal with the data.
 * @param onAdd Function called for adding a Category.
 * @param onShouldRevalidate Function called for requesting the refresh of the table data.
 */
const CategoryControls: React.FC<TableControlProps> = ({
  type,
  onAdd,
  onSave,
  onShouldRevalidate,
  onAddEditDialogClose,
  mode,
  editingFormData,
  onEnterHandler = () => undefined,
}) => {
  const { t } = useTranslation("common");
  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const [isAddEditDialogOpen, setAddEditDialogIsOpen] = useState(false);
  const [addEditDialogMode, setAddEditDialogMode] =
    useState<CategoryFormMode>("add");

  const onDialogClose = (success: boolean, message?: string) => {
    if (message != null) {
      enqueueSnackbar(message, { variant: success ? "success" : "error" });
    }

    if (success) {
      onShouldRevalidate?.();
    }

    onAddEditDialogClose();
  };

  useLayoutEffect(() => {
    if (mode != null) {
      setAddEditDialogMode(mode);
      setAddEditDialogIsOpen(true);
    } else {
      setAddEditDialogIsOpen(false);
    }
  }, [mode]);

  return (
    <>
      <div className={classes.root}>
        <HvInput
          className={classes.inputPlaceholder}
          aria-label={t("common:search.ariaLabel")}
          placeholder={t("common:search.placeholder")}
          type="search"
          onEnter={(event, value) => {
            onEnterHandler(value);
          }}
        />
        <HvButton
          className={classes.add}
          category="semantic"
          startIcon={<Add />}
          onClick={onAdd}
        >
          {t("common:buttons.add")}
        </HvButton>
      </div>
      <AddEditCategoryDialog
        isOpen={isAddEditDialogOpen}
        mode={addEditDialogMode}
        onSave={onSave}
        onClose={onDialogClose}
        type={type}
        initialValues={editingFormData}
      />
    </>
  );
};

export default CategoryControls;
